import { cn } from '@/lib/cn.ts';
import { type DisplaySeat, groupSeats } from '../lib/format.ts';

type Props = {
  seats: DisplaySeat[];
  /** lg = operational, walk-and-read sizing; sm = dense management view */
  size?: 'sm' | 'lg';
  className?: string;
};

/**
 * Reservation seats grouped by level → row. The row letter is shown once as a
 * crimson tab; individual seats render as mono chips so a doorman can read them
 * at a glance while escorting.
 */
export function SeatList({ seats, size = 'sm', className }: Props) {
  const groups = groupSeats(seats);
  const lg = size === 'lg';

  return (
    <div className={cn('space-y-4', className)}>
      {groups.map((g) => (
        <div key={g.level}>
          <div className="mb-2 flex items-baseline justify-between">
            <span className="kicker">{g.levelLabel}</span>
            <span className="mono text-[11px] text-text-faint tnum">{g.count} koltuk</span>
          </div>
          <div className="space-y-1.5">
            {g.rows.map((row) => (
              <div key={row.rowLabel} className="flex items-center gap-2">
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center justify-center rounded-md bg-accent-quiet font-display text-accent',
                    lg ? 'h-9 w-9 text-base' : 'h-7 w-7 text-sm',
                  )}
                  aria-hidden="true"
                >
                  {row.rowLabel}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {row.seats.map((s) => (
                    <span
                      key={s.id ?? s.label}
                      className={cn(
                        'mono inline-flex items-center justify-center rounded-md border border-border bg-surface tnum text-text',
                        lg ? 'h-9 min-w-9 px-2.5 text-sm' : 'h-7 min-w-7 px-2 text-xs',
                      )}
                    >
                      {s.label.split('-')[1] ?? s.label}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
